import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Modal } from '@core/components/ui/Modal';
import { TextArea } from '@core/components/ui/TextArea';
import { Button } from '@core/components/ui/Button';
import { Spinner } from '@core/components/ui/Spinner';
import { BadRatingDiagnosisCard } from '@core/components/ui/BadRatingDiagnosisCard';

/**
 * FeedbackModal
 *
 * Captures the required reason for a "bad" rating, then — while the backend runs
 * the root-cause analysis — shows a spinner and finally the diagnosis card, so the
 * admin gets immediate, actionable feedback in-context.
 *
 * `onSubmit(feedback)` must return the created rating (with a nested `analysis`
 * object) or throw on error.
 */
export function FeedbackModal({ isOpen, onClose, onSubmit, onMarkReview }) {
  const { t } = useTranslation();
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');
  const [phase, setPhase] = useState('form'); // 'form' | 'analyzing' | 'result'
  const [analysis, setAnalysis] = useState(null);

  // Mark the just-produced diagnosis as "not conform" and refresh the card in place.
  const handleMarkReview = onMarkReview
    ? async (notConform, comment) => {
        const updated = await onMarkReview(notConform, comment);
        if (updated?.analysis) setAnalysis(updated.analysis);
        return updated;
      }
    : undefined;

  const resetState = () => {
    setFeedback('');
    setError('');
    setPhase('form');
    setAnalysis(null);
  };

  const handleSubmit = async () => {
    if (!feedback.trim()) {
      setError(t('conversations.feedbackRequired'));
      return;
    }
    setError('');
    setPhase('analyzing');
    try {
      const result = await onSubmit(feedback.trim());
      setAnalysis(result?.analysis || null);
      setPhase('result');
    } catch (err) {
      setError(err?.error || err?.message || t('conversations.diagnosis.submitError', 'Failed to submit rating'));
      setPhase('form');
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const renderFooter = () => {
    if (phase === 'analyzing') return null;
    if (phase === 'result') {
      return (
        <Button variant="primary" onClick={handleClose} className="cursor-pointer">
          {t('conversations.diagnosis.close', 'Close')}
        </Button>
      );
    }
    return (
      <>
        <Button variant="secondary" onClick={handleClose} className="cursor-pointer">
          {t('conversations.cancel')}
        </Button>
        <Button variant="primary" onClick={handleSubmit} className="cursor-pointer">
          {t('conversations.submit')}
        </Button>
      </>
    );
  };

  const titleByPhase = {
    form: t('conversations.rateBad'),
    analyzing: t('conversations.diagnosis.analyzingTitle', 'Analyzing the reason…'),
    result: t('conversations.diagnosis.resultTitle', 'Diagnosis'),
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={titleByPhase[phase]}
      size="md"
      footer={renderFooter()}
    >
      {phase === 'form' && (
        <div className="space-y-4">
          <p className="text-sm text-muted">{t('conversations.feedbackRequired')}</p>
          <TextArea
            value={feedback}
            onChange={(value) => {
              setFeedback(value);
              setError('');
            }}
            placeholder={t('conversations.feedbackPlaceholder')}
            rows={4}
            error={error}
          />
        </div>
      )}

      {phase === 'analyzing' && (
        <div className="flex flex-col items-center justify-center gap-3 py-8">
          <Spinner size="md" />
          <p className="text-sm text-muted text-center">
            {t('conversations.diagnosis.analyzingBody', 'Analyzing why this answer was rated bad…')}
          </p>
        </div>
      )}

      {phase === 'result' && (
        <div className="space-y-3">
          {analysis ? (
            <BadRatingDiagnosisCard analysis={analysis} onMarkReview={handleMarkReview} />
          ) : (
            <div className="rounded-lg border border-border-default bg-surface-alt p-4 text-sm text-muted">
              {t('conversations.diagnosis.unavailable', 'Feedback saved. Automatic diagnosis is not available.')}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

FeedbackModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onMarkReview: PropTypes.func,
};
